import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, Share2, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.blogDetail(slug));
        setArticle(response.data);
        const allResponse = await axios.get(`${API_ENDPOINTS.blog}?category=${response.data.category}&limit=3`);
        setRelatedArticles(allResponse.data.filter(a => a.slug !== slug).slice(0, 2));
      } catch (err) {
        console.error('Failed to fetch article:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-navy mb-4">Article non trouve</h2>
          <button onClick={() => navigate('/actualites')} className="px-6 py-3 rounded-full bg-navy text-white font-sans font-semibold">
            Retour aux actualites
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>{`${article.title} - HPD Actualites`}</title>
        <meta name="description" content={article.excerpt || ''} />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => navigate('/actualites')} className="text-white/80 hover:text-white mb-6 flex items-center text-sm font-sans">
                <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux actualites
              </button>
              <Badge className="bg-forest text-white px-3 py-1 mb-4">{article.category}</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 tracking-tight">{article.title}</h1>
              <div className="flex items-center space-x-6 text-white/70 text-sm font-sans">
                <div className="flex items-center space-x-2"><Calendar className="w-4 h-4" /><span>{formatDate(article.created_at)}</span></div>
                <div className="flex items-center space-x-2"><Eye className="w-4 h-4" /><span>{article.views} vues</span></div>
                <span>Par {article.author}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            {article.content.split('\n').map((paragraph, index) => {
              const lineKey = `line-${index}`;
              if (paragraph.startsWith('# ')) return <h2 key={lineKey} className="text-3xl font-serif font-bold text-navy mt-8 mb-4">{paragraph.slice(2)}</h2>;
              if (paragraph.startsWith('## ')) return <h3 key={lineKey} className="text-2xl font-serif font-bold text-navy mt-6 mb-3">{paragraph.slice(3)}</h3>;
              if (paragraph.startsWith('> ')) return <blockquote key={lineKey} className="border-l-4 border-forest pl-6 py-2 my-6 italic text-gray-600 bg-warmgray rounded-r-xl font-serif">{paragraph.slice(2)}</blockquote>;
              if (paragraph.startsWith('- ')) return <li key={lineKey} className="ml-6 mb-2 font-sans text-gray-700">{paragraph.slice(2)}</li>;
              if (paragraph.trim()) return <p key={lineKey} className="text-gray-700 leading-relaxed mb-4 font-sans">{paragraph}</p>;
              return null;
            })}
          </div>

          {/* Video */}
          {article.video_url && (
            <div className="mt-10 mb-8">
              <h3 className="text-xl font-serif font-bold text-navy mb-4">Video</h3>
              <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm">
                <video controls className="w-full max-h-[500px]" data-testid="article-video">
                  <source src={article.video_url} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture video.
                </video>
              </div>
            </div>
          )}

          {/* PDF Document */}
          {article.pdf_url && (
            <div className="mt-10 mb-8">
              <h3 className="text-xl font-serif font-bold text-navy mb-4">Document</h3>
              <a
                href={article.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-pdf-link"
                className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 font-sans text-sm font-semibold hover:bg-red-100 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-6h8v2H8v-2zm0-4h8v2H8v-2zm0 8h5v2H8v-2z"/></svg>
                Telecharger le bulletin PDF
              </a>
            </div>
          )}

          {/* Social Links */}
          {article.social_links && (
            <div className="mt-6 mb-8 flex flex-wrap gap-3">
              {article.social_links.facebook && (
                <a href={article.social_links.facebook} target="_blank" rel="noopener noreferrer" data-testid="article-facebook-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1877F2] text-white font-sans text-sm font-semibold hover:bg-[#1877F2]/90 transition-all">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Voir sur Facebook
                </a>
              )}
              {article.social_links.instagram && (
                <a href={article.social_links.instagram} target="_blank" rel="noopener noreferrer" data-testid="article-instagram-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white font-sans text-sm font-semibold hover:opacity-90 transition-all">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  Voir sur Instagram
                </a>
              )}
            </div>
          )}

          {article.tags && article.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-black/10">
              <div className="flex items-center flex-wrap gap-2">
                <Tag className="w-4 h-4 text-gray-400" />
                {article.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-navy border-navy/30 font-sans text-xs">{tag}</Badge>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 p-6 bg-white rounded-2xl border border-black/5">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-semibold font-sans text-sm">Partager cet article</span>
              <button className="inline-flex items-center px-4 py-2 rounded-full border border-navy/20 text-navy text-sm font-sans hover:bg-navy hover:text-white transition-all">
                <Share2 className="w-4 h-4 mr-2" /> Partager
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-warmgray">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-serif font-bold text-navy mb-8 tracking-tight">Articles Similaires</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedArticles.map(related => (
                <div
                  key={related.id}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all"
                  onClick={() => navigate(`/actualites/${related.slug}`)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <Badge className="bg-forest text-white text-xs mb-3">{related.category}</Badge>
                    <h3 className="text-xl font-serif font-bold text-navy mb-2 group-hover:text-forest transition-colors">{related.title}</h3>
                    <p className="text-gray-600 text-sm font-sans line-clamp-2">{related.excerpt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
